import { useMemo } from 'react'

import { t } from 'i18next'
import ReactSelect, {
  components,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  OptionProps,
} from 'react-select'

import { SelectOption } from './types'
import { AvatarProfile } from '@/Atomic/Atoms/AvatarProfile'
import { Icon } from '@/Atomic/Atoms/Icon'

import './UserMultiSelect.scss'

const initialsOf = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('')

const Option = (props: OptionProps<SelectOption, true>) => (
  <components.Option {...props}>
    <AvatarProfile
      initials={initialsOf(props.data.label)}
      className="user-chip__avatar"
    />
    <span className="user-chip__name">{props.data.label}</span>
    {props.isSelected && <Icon icon="check" color="info" />}
  </components.Option>
)

const MultiValueContainer = (
  props: MultiValueGenericProps<SelectOption, true>,
) => (
  <components.MultiValueContainer {...props}>
    <div className="user-chip">
      <AvatarProfile
        initials={initialsOf(props.data.label)}
        className="user-chip__avatar"
      />
      {props.children}
    </div>
  </components.MultiValueContainer>
)

const buildLockedRemove = (lockedId: string) =>
  function LockedRemove(props: MultiValueRemoveProps<SelectOption, true>) {
    if (props.data.value === lockedId) return null
    return <components.MultiValueRemove {...props} />
  }

interface UserMultiSelectProps {
  options: SelectOption[]
  value: SelectOption[]
  onChange: (next: SelectOption[]) => void
  label: string
  currentUserId: string
  isDisabled?: boolean
  hasError?: boolean
  lockCurrentUser?: boolean
}

export const UserMultiSelect = ({
  options,
  value,
  onChange,
  label,
  currentUserId,
  isDisabled,
  hasError,
  lockCurrentUser = true,
}: UserMultiSelectProps) => {
  const me = options.find((o) => o.value === currentUserId)
  const allSelected = value.length === options.length && options.length > 0

  const ensureMe = (list: SelectOption[]) =>
    !lockCurrentUser || !me || list.some((o) => o.value === currentUserId)
      ? list
      : [me, ...list]

  const selectComponents = useMemo(
    () => ({
      Option,
      MultiValueContainer,
      ...(lockCurrentUser
        ? { MultiValueRemove: buildLockedRemove(currentUserId) }
        : {}),
      IndicatorSeparator: () => null,
    }),
    [currentUserId, lockCurrentUser],
  )

  return (
    <div className="user-multi-select">
      <div className="user-multi-select__header">
        <label className="user-multi-select__label">{label}</label>
        <div className="user-multi-select__actions">
          <span className="user-multi-select__count">
            {value.length}/{options.length}
          </span>
          {!isDisabled && options.length > 1 && (
            <button
              type="button"
              className="user-multi-select__toggle"
              onClick={() => onChange(allSelected ? ensureMe([]) : options)}
            >
              {allSelected
                ? t('timeTracking.form.clearAll')
                : t('timeTracking.form.selectAll')}
            </button>
          )}
        </div>
      </div>
      <ReactSelect<SelectOption, true>
        isMulti
        isClearable={false}
        isDisabled={isDisabled}
        options={options}
        value={value}
        onChange={(next) => onChange(ensureMe(next ? [...next] : []))}
        classNamePrefix="user-select"
        className={hasError ? 'user-select--invalid' : ''}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        placeholder={t('timeTracking.form.user')}
        components={selectComponents}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  )
}
